// The selected saved App's Data, read through the NATIVE Data service for exactly one selection and shaped
// for the existing Data tab. Reads only: nothing is written on mount, refresh or retry. History, restore,
// export, reset and sources have no producer here and stay absent (never empty rows, never a dead control).
// The session is the one captured when the App was opened; a replaced session ends the reads through it
// (the bound fetch throws) and an answer for a retired selection is dropped.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createNativeDataApi,
  type NativeCollection,
  type NativeDataApi,
  type NativeDataSelection,
  type NativeDataset,
} from '../native-data-api';
import type { DataTabProps } from './data-tab';
import { projectRowsState, projectTable } from './native-data-model';
import type { RowsState } from './data-model';
import { SessionChangedError } from '@/lib/session-guard';

/** The tab reads a table one page of up to `pageRows` rows at a time, and "more" reads the next page
 * from where the last one ended (a page its byte budget cut short included) until `maxRows` are
 * shown. A cursor continues only the read it came from, its limit included, so every page asks for
 * the same number of rows. */
export const NATIVE_DATA_PAGES = { pageRows: 100, maxRows: 1000 } as const;

/** The rows shown so far followed by the next page's rows (a row already shown is not repeated),
 * never more than `maxRows`: a page cut short by its byte budget can end past the cap. */
function appendPage(shown: NativeDataset, page: NativeDataset): NativeDataset {
  const seen = new Set(shown.rows.map((row) => row.id));
  const all = [...shown.rows, ...page.rows.filter((row) => !seen.has(row.id))];
  const rows = all.slice(0, NATIVE_DATA_PAGES.maxRows);
  return {
    ...page,
    rows,
    returnedRows: rows.length,
    hasMore: page.hasMore || all.length > rows.length,
  };
}

type Table = { dataset: NativeDataset | null; state: RowsState };
const canReadMore = (dataset: NativeDataset) =>
  dataset.nextCursor !== null && dataset.rows.length < NATIVE_DATA_PAGES.maxRows;
type Listing =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'unavailable' }
  | { status: 'ready'; list: NativeCollection[] };

export function useNativeData(
  selection: NativeDataSelection | null,
  session: typeof fetch,
  explicit?: NativeDataApi,
): DataTabProps | null {
  const key = selection
    ? `${selection.artifactId}\0${selection.revisionId}\0${selection.versionId}\0${selection.purpose}`
    : '';
  const api = useMemo(
    () => explicit ?? (selection ? createNativeDataApi(selection, session) : null),
    // the selection key, not the object, is the identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, session, explicit],
  );
  const [listing, setListing] = useState<Listing>({ status: 'loading' });
  const [tables, setTables] = useState<Record<string, Table>>({});
  const [attempt, setAttempt] = useState(0);
  // Every read belongs to the selection it was made for; an older answer is dropped.
  const generation = useRef(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // A table's first page (`shown` null), or the page after the rows it shows.
  const read = useCallback(
    async (
      id: string,
      mine: number,
      current: NativeDataApi,
      shown: NativeDataset | null = null,
    ) => {
      setTables((all) => ({
        ...all,
        [id]: { ...(all[id] ?? { dataset: null }), state: { status: 'loading' } },
      }));
      let next: Table;
      try {
        const result = await current.query(id, {
          limit: NATIVE_DATA_PAGES.pageRows,
          ...(shown?.nextCursor ? { cursor: shown.nextCursor } : {}),
        });
        const dataset = result.ok
          ? shown
            ? appendPage(shown, result.dataset)
            : result.dataset
          : null;
        next = dataset
          ? { dataset, state: projectRowsState(dataset, canReadMore(dataset)) }
          : { dataset: null, state: { status: 'failed' } };
      } catch (error) {
        if (error instanceof SessionChangedError) {
          if (alive.current && mine === generation.current) {
            setListing({ status: 'unavailable' });
            setTables({});
          }
          return; // terminal: no rows are shown or retried
        }
        next = { dataset: null, state: { status: 'failed' } };
      }
      if (alive.current && mine === generation.current)
        setTables((all) => ({ ...all, [id]: next }));
    },
    [],
  );

  useEffect(() => {
    if (!api) return;
    const mine = ++generation.current;
    setListing({ status: 'loading' });
    setTables({});
    api
      .listCollections()
      .then((list) => {
        if (!alive.current || mine !== generation.current) return;
        setListing({ status: 'ready', list });
        for (const collection of list) void read(collection.id, mine, api);
      })
      .catch((error) => {
        if (!alive.current || mine !== generation.current) return;
        setListing({ status: error instanceof SessionChangedError ? 'unavailable' : 'failed' });
      });
    return () => {
      generation.current++;
    };
  }, [api, attempt, read]);

  return useMemo(() => {
    if (!api) return null;
    const list = listing.status === 'ready' ? listing.list : [];
    return {
      tables: list.map((collection) =>
        projectTable(collection, tables[collection.id]?.dataset ?? null),
      ),
      rows: Object.fromEntries(
        list.map((collection) => [
          collection.id,
          tables[collection.id]?.state ?? { status: 'loading' as const },
        ]),
      ),
      history: null,
      kept: [],
      pending: [],
      canExport: false,
      canRestore: false,
      canReset: false,
      canUndoRow: false,
      undoing: null,
      formatAt: (iso: string) => iso,
      ...(listing.status === 'ready' ? {} : { listing: listing.status }),
      on: {
        onExport: () => {},
        onRestoreToTime: () => {},
        onViewFiles: () => {},
        onCloseHistory: () => {},
        onUndo: () => {},
        onMore: (id: string) => {
          const shown = tables[id]?.dataset;
          if (shown && canReadMore(shown)) void read(id, generation.current, api, shown);
        },
        // A table that failed reads again from its first page.
        onRetry: (id: string) => {
          if (listing.status === 'unavailable') return;
          if (id === '' || listing.status !== 'ready') setAttempt((n) => n + 1);
          else void read(id, generation.current, api);
        },
        onRestorePoint: () => {},
        onManageSource: () => {},
        onReviewRemoval: () => {},
        onReset: () => {},
      },
    } satisfies DataTabProps;
  }, [api, listing, tables, read]);
}
